import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { trackAdImpression, trackAdClick } from "@/lib/analytics";
import "./AdSlot.css";

/**
 * Reusable ad slot. Fetches best active campaign for `slot` key and renders
 * according to format. Hidden when no active campaign.
 *
 * Props:
 *  - slot: slot key (e.g. "home_header_strip", "article_mid")
 *  - category: optional category_slug for sponsored_section matching
 *  - className: optional CSS wrapper class
 */
export default function AdSlot({ slot, category, className = "" }) {
  const [campaign, setCampaign] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const ref = useRef(null);
  const impressionSentRef = useRef(false);

  // Detect device for responsive filtering
  const device = (() => {
    if (typeof window === "undefined") return "desktop";
    return window.matchMedia?.("(max-width: 640px)")?.matches ? "mobile" : "desktop";
  })();

  useEffect(() => {
    let active = true;
    api.get(`/ads/slot/${slot}`, { params: { device, category: category || undefined } })
      .then((r) => { if (active) { setCampaign(r.data?.campaign || null); setLoaded(true); } })
      .catch(() => { if (active) setLoaded(true); });
    return () => { active = false; };
  }, [slot, category, device]);

  // Viewability tracking: fire impression once >=50% in viewport for >=1s
  useEffect(() => {
    if (!campaign || !ref.current) return;
    const el = ref.current;
    let timer = null;
    const obs = new IntersectionObserver((entries) => {
      const e = entries[0];
      if (e.isIntersecting && e.intersectionRatio >= 0.5) {
        timer = setTimeout(() => {
          if (!impressionSentRef.current) {
            trackAdImpression(campaign.id);
            impressionSentRef.current = true;
          }
        }, 1000);
      } else if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    }, { threshold: [0, 0.5, 1] });
    obs.observe(el);
    return () => { if (timer) clearTimeout(timer); obs.disconnect(); };
  }, [campaign]);

  const handleClick = async (e, destOverride) => {
    e.preventDefault();
    const dest = destOverride || campaign?.destination_url;
    trackAdClick(campaign.id, dest);
    try {
      const r = await api.post(`/ads/click/${campaign.id}`);
      const url = r.data?.destination_url || dest;
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    } catch (_) {
      if (dest) window.open(dest, "_blank", "noopener,noreferrer");
    }
  };

  if (!loaded) return null;
  if (!campaign) return null;

  // Device filter double-check client-side
  if (campaign.device && campaign.device !== "both" && campaign.device !== device) return null;

  const label = campaign.label || "Contenido patrocinado";

  // Format renderers
  switch (campaign.format) {
    case "micro_sponsor":
      return (
        <div ref={ref} className={`adslot adslot-micro ${className}`} data-testid={`adslot-${slot}`}>
          <span className="adslot-label">{label}</span>
          <a href={campaign.destination_url || "#"} onClick={handleClick} aria-label={campaign.advertiser}>
            {campaign.logo_url && <img src={campaign.logo_url} alt={campaign.advertiser} />}
            <span className="adslot-advertiser">{campaign.advertiser}</span>
          </a>
        </div>
      );

    case "banner":
      return (
        <div ref={ref} className={`adslot adslot-banner ${className}`} data-testid={`adslot-${slot}`}>
          <span className="adslot-label-top">{label}</span>
          <a href={campaign.destination_url || "#"} onClick={handleClick} className="adslot-banner-link">
            {campaign.image_url && <img src={campaign.image_url} alt={campaign.advertiser} loading="lazy" />}
          </a>
        </div>
      );

    case "native_card":
      return (
        <article ref={ref} className={`adslot adslot-native ${className}`} data-testid={`adslot-${slot}`}>
          <span className="adslot-native-badge">{label}</span>
          <a href={campaign.destination_url || "#"} onClick={handleClick} className="adslot-native-link">
            {campaign.image_url && <img src={campaign.image_url} alt="" loading="lazy" />}
            <div className="adslot-native-body">
              <h3>{campaign.headline || campaign.name}</h3>
              {campaign.description && <p>{campaign.description}</p>}
              <span className="adslot-native-sponsor">{campaign.advertiser}</span>
            </div>
          </a>
        </article>
      );

    case "sidebar":
      return (
        <aside ref={ref} className={`adslot adslot-sidebar ${className}`} data-testid={`adslot-${slot}`}>
          <span className="adslot-label-top">{label}</span>
          <a href={campaign.destination_url || "#"} onClick={handleClick}>
            {campaign.image_url && <img src={campaign.image_url} alt={campaign.advertiser} loading="lazy" />}
          </a>
        </aside>
      );

    case "sponsored_section":
      return (
        <div ref={ref} className={`adslot adslot-section ${className}`} data-testid={`adslot-${slot}`}>
          <span className="adslot-label">Presentado por</span>
          <a href={campaign.destination_url || "#"} onClick={handleClick}>
            {campaign.logo_url && <img src={campaign.logo_url} alt={campaign.advertiser} loading="lazy" />}
            {!campaign.logo_url && <span className="adslot-advertiser">{campaign.advertiser}</span>}
          </a>
        </div>
      );

    case "logo_strip":
      return (
        <div ref={ref} className={`adslot adslot-strip ${campaign.strip_scrolling ? "scrolling" : ""} ${className}`} data-testid={`adslot-${slot}`}>
          <span className="adslot-label">{label}</span>
          <div className="adslot-strip-track" style={campaign.strip_scrolling ? { animationDuration: `${campaign.strip_scroll_speed || 30}s` } : {}}>
            {(campaign.logos || []).map((l, i) => (
              <a
                key={i}
                href={l.destination_url || "#"}
                onClick={(e) => handleClick(e, l.destination_url)}
                aria-label={l.name || `Sponsor ${i + 1}`}
                className="adslot-strip-logo"
              >
                {l.logo_url && <img src={l.logo_url} alt={l.name || ""} loading="lazy" />}
              </a>
            ))}
          </div>
        </div>
      );

    case "branded_content":
      // Branded content is rendered via Article pages (not via AdSlot).
      return null;

    default:
      return null;
  }
}
